import classNames from 'classnames';
import React from 'react';

import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {hasMissingRequiredFields} from 'inventory/required-fields/missingRequiredFieldsUtil';
import useShowMissingRequiredFieldWarnings from 'inventory/required-fields/useShowMissingRequiredFieldWarnings';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  doesCurrentUserHaveAccessForItem,
  isItemAvailable,
  doesItemHaveBlockingStatusWarning,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {openLocationExpandedSearch} from 'samples/StorableActions';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import SmartChip from 'ui-library/chips/SmartChip';
import useSmartChipQuery, {SmartChipType} from 'ui-library/chips/useSmartChipQuery';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

import {StorableChipSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableChipSummaryGraphQL';
import {StorableChipPopoverContent} from './StorableChipPopoverContent';

type LocationChipSummary = ExtractByTypename<StorableChipSummaryGraphQL, 'LocationNode'>;

type ContainerChipSummary = ExtractByTypename<StorableChipSummaryGraphQL, 'ContainerNode'>;

export interface StorableChipProps extends SharedSmartChipProps<SmartChipType.STORABLE> {
  /** The API identifier of the storable */
  id: string;
  /**
   * A function to override how this storable's contents are rendered in the fancy chip popover. By default,
   * the popover renders a BenchChip for each content. Intended for cases where a custom visual treatment for
   * each content is necessary, as with Plate Maps, which inject a color-coded swatch next to each content
   * chip.
   */
  popoverContainedItemRenderer?: (id: string) => JSX.Element;
}

function isReadonly(storable?: StorableChipSummaryGraphQL): boolean {
  if (!storable) {
    return false;
  }
  return !isFeatureEnabled({feature: StandardFeature.OPEN, item: storable});
}

function isContainerChipSummary(storable: StorableChipSummaryGraphQL): storable is ContainerChipSummary {
  return storable.__typename === 'ContainerNode';
}

function isLocationChipSummary(storable: StorableChipSummaryGraphQL): storable is LocationChipSummary {
  return storable.__typename === 'LocationNode';
}

interface StorableChipStatus {
  /** The current user can't act on the storable, e.g. a restricted sample they aren't a user of. */
  hasBlockingStatus: boolean;
  /** The storable is unavailable, unauthorized, or expired. */
  hasWarningStatus: boolean;
  isValidationFailed: boolean;
  showMissingRequiredFields: boolean;
}

/**
 * Blocking statuses are red. Other statuses are orange unless missing required fields already make the
 * chip red.
 */
function getStorableChipClassName(className: string | null | undefined, status: StorableChipStatus): string {
  return classNames(className, {
    'chip--negative': status.hasBlockingStatus,
    'chip--warningLighter':
      status.hasWarningStatus && !status.hasBlockingStatus && !status.showMissingRequiredFields,
  });
}

export default function StorableChip(props: StorableChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item,
    handleClickLink,
    isInaccessible,
    itemUrl: storableUrl,
  } = useSmartChipQuery<SmartChipType.STORABLE>({
    id,
    onClick,
    getUrlFn: (storable) =>
      isContainerChipSummary(storable) || isLocationChipSummary(storable) ? storable.url : '',
  });
  const {isExportView} = EditorPropertiesContext.useContext();
  const showMissingRequiredFieldWarnings = useShowMissingRequiredFieldWarnings();
  const storable = item != null ? {...item, parentStorable: item.authenticatedParentStorable} : item;

  const handleClickLinkEnhanced = (e: BasicEvent): void => {
    if (storable) {
      if (props.onClick != null) {
        handleClickLink(e);
      } else {
        e.stopPropagation();
        if (isLocationChipSummary(storable)) {
          openLocationExpandedSearch(graphQLToREST('LocationSummary', storable));
        } else {
          FileBrowserActions.openFile(storable.id, {folderItem: {...storable}});
        }
      }
    }
  };

  const showUnauthorized =
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) && !doesCurrentUserHaveAccessForItem(storable);
  const showUnavailable = !isItemAvailable(storable);
  const showExpired = storable
    ? BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE &&
      isContainerChipSummary(storable) &&
      storable.expirationInfo.isTrackingEnabled &&
      storable.expirationInfo.isExpired
    : false;
  const showMissingRequiredFields = showMissingRequiredFieldWarnings && hasMissingRequiredFields(storable);
  const status: StorableChipStatus = {
    hasBlockingStatus:
      storable != null &&
      (showUnauthorized || showUnavailable) &&
      doesItemHaveBlockingStatusWarning(storable),
    hasWarningStatus: showUnauthorized || showUnavailable || showExpired,
    isValidationFailed: isFailed(storable?.validationState),
    showMissingRequiredFields,
  };

  // Storable chips always render a popover, which shows a banner or row for each status, so the chip
  // itself only signals them through color and icon.
  return (
    <SmartChip
      {...sharedSmartChipProps}
      className={getStorableChipClassName(props.className, status)}
      negative={status.isValidationFailed || status.showMissingRequiredFields}
      iconOverride={showMissingRequiredFields ? 'exclamation-circle' : sharedSmartChipProps.iconOverride}
      icon={(storable?.storableSchema?.iconId as BenchlingIconName) ?? null}
      item={storable}
      isInaccessible={isInaccessible}
      isReadonly={isReadonly(storable)}
      onClick={handleClickLinkEnhanced}
      url={storableUrl}
      popoverContentOverride={
        !isExportView ? (
          <StorableChipPopoverContent
            id={props.id}
            isInaccessible={isInaccessible}
            storable={storable}
            containedItemRenderer={props.popoverContainedItemRenderer}
          />
        ) : undefined
      }
    />
  );
}
